import { Tag } from "primereact/tag";
import styled from "styled-components";

import { convertToPercent } from "../helpers";

const StyledTag = styled(Tag)`
  border-radius: 20px;
  padding-left: 10px;
  padding-right: 10px;
  color: var(--green-500) !important;
  background-color: var(--green-50) !important;
  .p-tag-value {
    font-size: 10px;
  }
`;

/** CLE-2820 — same family as "Selected", in its own colour so the two never read as one. */
const StyledAiTag = styled(StyledTag)`
  color: var(--indigo-600) !important;
  background-color: var(--indigo-50) !important;
`;

export function BenefitLine({
  title,
  note,
  limit,
  value,
  isCoinsurance,
  benefitEntities,
  networkIndicator,
  coverageLevel,
  messages,
  timePeriodQualifier,
  showSelected,
  showAiRecommendation,
  dates,
}: {
  title: string;
  note?: string | null;
  /** The limit this remaining figure counts down from, e.g. "of $5,000.00 · $743.74 met". */
  limit?: string | null;
  value?: string | null;
  dates?: { Date?: string | null; Type?: string | null }[];
  isCoinsurance?: boolean;
  benefitEntities?: any;
  networkIndicator?: string;
  coverageLevel?: string;
  messages?: string[];
  timePeriodQualifier?: string;
  showSelected?: boolean;
  /** CLE-2820 — SuperAdmin-only label; the caller decides who sees it. */
  showAiRecommendation?: boolean;
}) {
  const getNetworkDisplay = (indicator: string) => {
    switch (indicator) {
      case "Yes":
        return "In-Network";
      case "No":
        return "Out-of-Network";
      case "Both":
        return "In-Network & Out-of-Network";
      default:
        return indicator;
    }
  };

  return (
    <div className="w-full flex justify-content-between pr-4 pl-3">
      <div className="flex flex-column gap-1">
        <div className="flex flex-row gap-2 items-center">
          <p className="text-sm">{title}</p>
          {showSelected && (
            <StyledTag
              value="Selected"
              severity="success"
              className="text-xs"
            />
          )}
          {showAiRecommendation && (
            <StyledAiTag
              value="AI Recommendation"
              severity="info"
              className="text-xs"
            />
          )}
        </div>
        {limit ? <p className="text-sm text-gray-600 pl-3">{limit}</p> : null}
        {note ? <p className="text-sm text-gray-600 pl-3">{note}</p> : null}
        {networkIndicator && (
          <p className="text-sm text-blue-600 pl-3">
            Network: {getNetworkDisplay(networkIndicator)}
          </p>
        )}
        {timePeriodQualifier && (
          <p className="text-sm text-orange-600 pl-3">
            Period: {timePeriodQualifier}
          </p>
        )}
        {messages && messages.length > 0 && (
          <div className="pl-3">
            {messages.map((message, index) => (
              <p key={index} className="text-sm text-gray-600">
                {message}
              </p>
            ))}
          </div>
        )}
        {isCoinsurance && benefitEntities?.length
          ? benefitEntities.map((benefit: any, index: number) => (
              <p
                className="text-sm text-gray-600 pl-3"
                key={`${index}_benefit`}
              >
                {benefit?.entityType || "N/A"} - {benefit?.name || "N/A"}
              </p>
              // eslint-disable-next-line indent
            ))
          : null}
      </div>
      <div className="flex flex-column align-items-end">
        {(value || (!value && !dates?.length)) && (
          <p className="text-primary-500 font-semibold">
            {(() => {
              if (value == null || value === "") return "N/A";
              if (!Number.isNaN(Number(value))) return convertToPercent(value);
              return value;
            })()}
          </p>
        )}
        {dates?.map((d: any, i: number) => (
          <div className="flex flex-column align-items-end" key={i}>
            <p className="text-primary-500 font-semibold">{d.Date}</p>
            <p className="text-gray-500 font-medium text-xs">{d.Type}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
